import { useCallback, useMemo } from 'react';
import { pick } from 'lodash';

import { getFragmentData } from '@e-socna/data-layer';
import { ensureDefined } from '@e-socna/utils';
import type {
  AccountingWorkflowId,
  AccountingStateId,
  Id,
  VatId,
} from '@e-socna/data-layer';

import { useMissionAccess } from '../../components/Access/hooks/useMissionAccess.hook';
import { FolderBusinessEntityState } from '../../components/FolderBusinessEntityState';
import { DocumentEditorResolvers_QuickAccess_Mission_Fragment } from '../../components/DocumentEditor/resolvers/fragments/quick-access.fragment';
import { getLiveServiceUsagesGroups } from '../../components/DocumentEditor/resolvers/utils/mission-service-usages-groups.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import type { MissionAccountingPage_AccountingInfo } from '../../pages/MissionAccounting/fragments/accounting-info.fragment';

import type { RenderActionDialog } from '../FolderBusinessEntityState/types/action-dialog-render-props.type';

import { AggregateServiceUsagesFields } from './AggregateServiceUsagesFields';
import { useFindAllMissionAccountingInfoActionPermissionsQuery } from './hooks/useFindAllMissionAccountingInfoActionPermissionsQuery.hook';
import { useUpdateMissionAccountingInfoStateSubmitFactory } from './hooks/useUpdateMissionAccountingInfoStateSubmitFactory.hook';
import { buildEffectiveAggregationLevelGroups } from './utils/build-aggregation-level-groups.util';
import {
  isServiceUsageAggregationLevelAvailable,
  resolveDefaultAggregationLevel,
} from './utils/aggregation-level-options.util';
import { computeAggregationLevelVatWarnings } from './utils/compute-aggregation-level-vat-warnings.util';
import {
  AGGREGATION_ACTION_DIALOG_TITLE,
  isAggregationActionDialogNeeded,
  isAggregationChoiceValid,
} from './utils/aggregation-action-dialog.util';

interface Props {
  readonly enterEditionMode: () => void;
  readonly isInEditionMode: boolean;
  readonly leaveEditionMode: () => void;
  readonly missionId: Id;
  readonly missionAccountingInfo: MissionAccountingPage_AccountingInfo;
  readonly onSuccess?: (params: { actionId: Id; newStateId: Id }) => void;
}

export const MissionAccountingInfoStateForm = ({
  enterEditionMode,
  isInEditionMode,
  leaveEditionMode,
  missionId,
  missionAccountingInfo,
  onSuccess,
}: Readonly<Props>) => {
  const { accountingStatesIdMap, accountingWorkflowsIdMap, vatsIdMap } =
    useReferencesContext();

  const { mission } = useMissionReadyContext();

  const quickAccess = getFragmentData(
    DocumentEditorResolvers_QuickAccess_Mission_Fragment,
    mission,
  );

  const aggregationLevelGroups = useMemo(
    () =>
      buildEffectiveAggregationLevelGroups({
        selectedOptionGroupIds:
          quickAccess.quotationInfo?.snapshot?.selectedOptionGroupIds,
        liveGroups: getLiveServiceUsagesGroups(quickAccess),
        snapshotGroups:
          quickAccess.quotationInfo?.snapshot?.serviceUsagesGroups,
      }),
    [quickAccess],
  );

  const isServiceUsageLevelAvailable = isServiceUsageAggregationLevelAvailable(
    aggregationLevelGroups,
  );

  const defaultAggregationLevel = resolveDefaultAggregationLevel(
    aggregationLevelGroups,
  );

  const vatWarningsByLevel = useMemo(
    () =>
      computeAggregationLevelVatWarnings({
        groups: aggregationLevelGroups,
        resolveVatRateLabel: (vatId) =>
          vatsIdMap[vatId as VatId]?.label ?? 'TVA inconnue',
      }),
    [aggregationLevelGroups, vatsIdMap],
  );

  const { accountingState, accountingWorkflow } = ensureDefined({
    accountingWorkflow:
      accountingWorkflowsIdMap[
        missionAccountingInfo.workflowId as AccountingWorkflowId
      ],
    accountingState:
      accountingStatesIdMap[missionAccountingInfo.stateId as AccountingStateId],
  });

  const { currentNode } = ensureDefined(
    {
      currentNode: accountingWorkflow.stateMachine.find(
        (node) => node.stateId === accountingState._id,
      ),
    },
    'Current node not found',
  );

  const { data, isFetching } =
    useFindAllMissionAccountingInfoActionPermissionsQuery(
      missionId,
      isInEditionMode,
    );

  const useUpdateMissionAccountingInfoStateSubmit =
    useUpdateMissionAccountingInfoStateSubmitFactory({ missionId, onSuccess });

  const currentNodeActions = currentNode.actions;

  const renderActionDialog: RenderActionDialog = useCallback(
    (formikBag) => {
      const selectedAction = currentNodeActions.find(
        (action) => action._id === formikBag.values.actionId,
      );

      if (!isAggregationActionDialogNeeded(selectedAction)) {
        return null;
      }

      return {
        title: AGGREGATION_ACTION_DIALOG_TITLE,
        // eslint-disable-next-line dot-notation -- index signature access required by noPropertyAccessFromIndexSignature
        isValid: isAggregationChoiceValid(formikBag.values['aggregationLevel']),
        content: (
          <AggregateServiceUsagesFields
            defaultAggregationLevel={defaultAggregationLevel}
            isServiceUsageLevelAvailable={isServiceUsageLevelAvailable}
            key={selectedAction?._id}
            vatWarningsByLevel={vatWarningsByLevel}
          />
        ),
      };
    },
    [
      currentNodeActions,
      defaultAggregationLevel,
      isServiceUsageLevelAvailable,
      vatWarningsByLevel,
    ],
  );

  const currentNodeWithAccountingState = useMemo(
    () => ({
      ...currentNode,
      ...pick(accountingState, ['label', 'description', 'chip']),
    }),
    [currentNode, accountingState],
  );

  const { changeAccountingState } = useMissionAccess(
    missionId,
    missionAccountingInfo,
  );

  return (
    <FolderBusinessEntityState
      actionPermissions={data?.findOneMission.access.actions}
      areActionPermissionsLoading={isFetching}
      currentNode={currentNodeWithAccountingState}
      enableAction
      formTitle="Mise à jour du Statut de la Comptabilité"
      gate={changeAccountingState}
      isInEditionMode={isInEditionMode}
      onCancel={leaveEditionMode}
      onEdit={enterEditionMode}
      renderActionDialog={renderActionDialog}
      useUpdateStateSubmit={useUpdateMissionAccountingInfoStateSubmit}
    />
  );
};
